import { TableChildRow, TableRow } from "@carbon6/c6-frontend-design";
import type { IsaacEvaluationScenarioReq } from "@/api/training";
import type { TrainingEvalScenarioView } from "@/api/trainingEval";
import type { AsyncListState } from "../useAsyncList";

// Schedule recipe 패널의 Eval scenario 표 행 모델. 최상위 행은 recipe 의 scenario 다. 그 scenario 의
// parameter 는 하위 행(TableChildRow)이라 c6 가 scenario 행 바로 아래에 그린다. recipe 는 scenario 의
// id·이름·seed 만 포함한다. 그래서 parameter 는 카탈로그(`fixed_env_vars`·`tunable_env_vars`)에서 id 로 찾는다.

export type ScenarioCatalogState = Pick<
  AsyncListState<TrainingEvalScenarioView>,
  "items" | "status"
>;

// c6 DataTableColumn.key 는 행 프로퍼티로 제한되므로 세 컬럼의 키를 여기서 선언한다.
export class ScheduleRecipeScenarioRow extends TableRow {
  readonly name: string;
  readonly scenarioId: string;
  readonly seed: number | null;
  private readonly rowIndex: number;
  private readonly children: readonly ScheduleRecipeScenarioChildRow[];

  constructor(scenario: IsaacEvaluationScenarioReq, index: number, catalog: ScenarioCatalogState) {
    super(null);
    this.name = scenario.name;
    this.scenarioId = scenario.scenario_id;
    this.seed = scenario.seed ?? null;
    // 같은 scenario 가 두 번 들어올 수 있으므로 위치를 id 에 포함한다.
    this.rowIndex = index;
    this.children = scenarioChildren(this.getRowId(), scenario.scenario_id, catalog);
  }

  getRowId(): string {
    return `scenario:${this.rowIndex}:${this.scenarioId}`;
  }

  canShow(): boolean {
    return true;
  }

  // 이 표에는 선택 UI 가 없다.
  canSelect(): boolean {
    return false;
  }

  // 펼침 값은 정하지 않으므로 표의 defaultRowChildrenExpanded 가 채운다. 접기 UI 가 없어 항상 펼친다.
  override getRowChildren(): readonly ScheduleRecipeScenarioChildRow[] {
    return this.children;
  }
}

// 하위 행. parameter 한 건이거나, parameter 를 표시할 수 없는 이유 한 줄이다.
export class ScheduleRecipeScenarioChildRow extends TableChildRow {
  constructor(
    parentRowId: string,
    readonly content:
      | { kind: "param"; key: string; value: string }
      | { kind: "note"; text: string },
  ) {
    super(parentRowId);
  }

  getRowId(): string {
    const suffix = this.content.kind === "param" ? `param:${this.content.key}` : "note";
    return `${this.getParentRowId()}/${suffix}`;
  }

  canShow(): boolean {
    return true;
  }

  canSelect(): boolean {
    return false;
  }
}

function scenarioChildren(
  parentRowId: string,
  scenarioId: string,
  catalog: ScenarioCatalogState,
): ScheduleRecipeScenarioChildRow[] {
  const note = (text: string) => [
    new ScheduleRecipeScenarioChildRow(parentRowId, { kind: "note", text }),
  ];
  if (catalog.status === "error") {
    return note("Scenario 카탈로그를 불러오지 못했습니다.");
  }
  if (catalog.status !== "success") {
    return note("Parameter를 불러오고 있습니다.");
  }
  const found = catalog.items.find((item) => item.id === scenarioId);
  if (found === undefined) {
    return note("카탈로그에서 이 Scenario를 찾지 못했습니다.");
  }
  // 평가 결과 화면(`scenarioParams`)과 같은 방식으로 합친다. 같은 key 면 tunable 이 우선한다.
  const params = Object.entries({ ...found.fixed_env_vars, ...found.tunable_env_vars });
  if (params.length === 0) {
    return note("Parameter가 없습니다.");
  }
  return params.map(
    ([key, value]) =>
      new ScheduleRecipeScenarioChildRow(parentRowId, { kind: "param", key, value: String(value) }),
  );
}
